/**
 * Blob mascot choreographies. CMS stores a preset id on the hosting section;
 * this map owns color, motion, placement, and timing.
 *
 * Peek / blink / bounce / roll match "Blob Mascot — Motion States".
 */

export const MASCOT_COLORS = {
  teal: '#21aaa3',
  lime: '#d8fb4b',
  purple: '#5f5bf7',
} as const;

export type MascotColor = keyof typeof MASCOT_COLORS;

export type MascotMotion =
  | 'peek-top'
  | 'peek-left'
  | 'peek-right'
  | 'bounce'
  | 'roll'
  | 'peer';

export type MascotAnchor =
  | 'bottom-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom'
  | 'section-left'
  | 'section-right'
  /** Behind the row's right-edge card, peeking up over its top. */
  | 'section-first-card'
  /** Behind the first card, peeking out of its left edge. */
  | 'card-left'
  /** Behind the row's right-edge card, peeking out of its right edge. */
  | 'card-right';

/** Anchors that must be rendered inside a host card (see CardGrid). */
export const CARD_ANCHORS: ReadonlyArray<MascotAnchor> = [
  'section-first-card',
  'card-left',
  'card-right',
];

export type MascotStep = {
  color: MascotColor;
  motion: MascotMotion;
  anchor: MascotAnchor;
  delayMs: number;
  /** Peek cycle length (board keyframes are %-based). Always one-shot. */
  durationMs: number;
};

/** Hero intro choreographies (dropdown on hero sections). */
export const HERO_MASCOT_PRESETS = [
  'none',
  'peek-teal-ul-then-lime-lr',
  'peek-lime-lr',
  'peek-purple-ll',
  'bounce-teal-bottom-to-cta',
  'roll-purple-bottom',
  'peek-purple-ul-then-teal-lc',
] as const;

export type HeroMascotPreset = (typeof HERO_MASCOT_PRESETS)[number];

/** Mid-page peer choreographies (e.g. Card Grid). */
export const SECTION_MASCOT_PRESETS = [
  'none',
  'peer-sides-purple-teal',
  'peer-left-purple',
  'peer-right-teal',
  'peer-right-lime',
  'peer-behind-first-card-lime',
  'peer-behind-first-card-teal',
  'peer-behind-first-card-purple',
] as const;

export type SectionMascotPreset = (typeof SECTION_MASCOT_PRESETS)[number];

export const MASCOT_HREF = '/rails';

/**
 * Board pop cycle is 12s with ~7s hold. Brief asks ~5s visible then hide.
 * 8s keeps board % keyframes and lands the exit near 5.5s (69.5% of 8s).
 */
const PEEK_MS = 8000;

/** Heroes hold for a beat after load before the mascot makes its entrance. */
export const HERO_START_DELAY_MS = 2000;

export const HERO_MASCOT_CHOREOGRAPHIES: Record<
  Exclude<HeroMascotPreset, 'none'>,
  MascotStep[]
> = {
  // The "UL" (under-the-logo) steps were removed from the heroes; the stored
  // preset keys are kept so existing CMS content keeps working.
  'peek-teal-ul-then-lime-lr': [
    {
      color: 'teal',
      motion: 'peek-left',
      anchor: 'bottom-right',
      delayMs: HERO_START_DELAY_MS,
      durationMs: PEEK_MS,
    },
  ],
  'peek-lime-lr': [
    {
      color: 'lime',
      motion: 'peek-left',
      anchor: 'bottom-right',
      delayMs: HERO_START_DELAY_MS,
      durationMs: PEEK_MS,
    },
  ],
  'peek-purple-ll': [
    {
      color: 'purple',
      motion: 'peek-right',
      anchor: 'bottom-left',
      delayMs: HERO_START_DELAY_MS,
      durationMs: PEEK_MS,
    },
  ],
  'bounce-teal-bottom-to-cta': [
    {
      color: 'teal',
      motion: 'bounce',
      anchor: 'bottom',
      delayMs: HERO_START_DELAY_MS + 400,
      // Keyframe percentages in blob-mascot.css are cut to this length.
      durationMs: 4520,
    },
  ],
  'roll-purple-bottom': [
    {
      color: 'purple',
      motion: 'roll',
      anchor: 'bottom',
      delayMs: HERO_START_DELAY_MS + 400,
      durationMs: 4200,
    },
  ],
  'peek-purple-ul-then-teal-lc': [
    {
      color: 'teal',
      motion: 'peek-top',
      anchor: 'bottom-center',
      delayMs: HERO_START_DELAY_MS,
      durationMs: PEEK_MS,
    },
  ],
};

export const SECTION_MASCOT_CHOREOGRAPHIES: Record<
  Exclude<SectionMascotPreset, 'none'>,
  MascotStep[]
> = {
  'peer-sides-purple-teal': [
    {
      color: 'purple',
      motion: 'peer',
      anchor: 'card-left',
      delayMs: 200,
      durationMs: PEEK_MS,
    },
    {
      color: 'teal',
      motion: 'peer',
      anchor: 'card-right',
      delayMs: 600,
      durationMs: PEEK_MS,
    },
  ],
  // Hero analogs: peek-purple-ll / peek-teal / peek-lime-lr → card edges.
  'peer-left-purple': [
    {
      color: 'purple',
      motion: 'peer',
      anchor: 'card-left',
      delayMs: 200,
      durationMs: PEEK_MS,
    },
  ],
  'peer-right-teal': [
    {
      color: 'teal',
      motion: 'peer',
      anchor: 'card-right',
      delayMs: 200,
      durationMs: PEEK_MS,
    },
  ],
  'peer-right-lime': [
    {
      color: 'lime',
      motion: 'peer',
      anchor: 'card-right',
      delayMs: 200,
      durationMs: PEEK_MS,
    },
  ],
  'peer-behind-first-card-lime': [
    {
      color: 'lime',
      motion: 'peer',
      anchor: 'section-first-card',
      delayMs: 200,
      durationMs: PEEK_MS,
    },
  ],
  // Hero analog: peek teal (bottom centre) → eyes over the right-edge card.
  'peer-behind-first-card-teal': [
    {
      color: 'teal',
      motion: 'peer',
      anchor: 'section-first-card',
      delayMs: 200,
      durationMs: PEEK_MS,
    },
  ],
  'peer-behind-first-card-purple': [
    {
      color: 'purple',
      motion: 'peer',
      anchor: 'section-first-card',
      delayMs: 200,
      durationMs: PEEK_MS,
    },
  ],
};

export function isHeroMascotPreset(
  value: string | null | undefined,
): value is Exclude<HeroMascotPreset, 'none'> {
  return Boolean(
    value && value !== 'none' && value in HERO_MASCOT_CHOREOGRAPHIES,
  );
}

/**
 * Presets whose steps all anchor to cards must be rendered inside their host
 * cards (so the blob can hide behind the card background) rather than at the
 * section level.
 */
export function isCardMascotPreset(
  value: string | null | undefined,
): value is Exclude<SectionMascotPreset, 'none'> {
  return (
    isSectionMascotPreset(value) &&
    SECTION_MASCOT_CHOREOGRAPHIES[value].every((step) =>
      CARD_ANCHORS.includes(step.anchor),
    )
  );
}

/**
 * Bounce / roll travel along the section floor — host sections should leave
 * extra bottom padding so the lane stays clear of the cards.
 * Uses the same preset ids as the home hero.
 */
export function isFloorMascotPreset(value: string | null | undefined): boolean {
  return (
    value === 'bounce-teal-bottom-to-cta' || value === 'roll-purple-bottom'
  );
}

export function isSectionMascotPreset(
  value: string | null | undefined,
): value is Exclude<SectionMascotPreset, 'none'> {
  return Boolean(
    value && value !== 'none' && value in SECTION_MASCOT_CHOREOGRAPHIES,
  );
}
